import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { ErrorIcon } from "@repo/ui-parkmobile/icons/ErrorIcon";

const phoneInputWrapper = tv({
  base: "uipm:relative uipm:w-full",
});

const phoneInputContainer = tv({
  base: cn(
    "uipm:w-full uipm:rounded-xl",
    "uipm:px-4 uipm:py-3 uipm:min-h-[56px]",
    "uipm:flex uipm:flex-row uipm:gap-2 uipm:items-center",
    "uipm:border-2 uipm:border-transparent uipm:transition-colors",
    "uipm:focus-within:border-[var(--color-color-light)]"
  ),
  variants: {
    background: {
      default: "uipm:form-hover",
      white: "uipm:bg-white uipm:hover:bg-[var(--surface-surface-secondary)]",
    },
    state: {
      default: "",
      error: "uipm:border-[var(--color-color-danger)] uipm:pr-12",
      disabled: "uipm:opacity-50 uipm:cursor-not-allowed",
    },
  },
  defaultVariants: {
    background: "default",
    state: "default",
  },
});

const phoneInput = tv({
  base: cn(
    "uipm:flex-1 uipm:bg-transparent",
    "uipm:text-base uipm:leading-[24px]",
    "uipm:text-[var(--typography-colors-text-body)] uipm:placeholder:text-[var(--typography-colors-text-faded)]",
    "uipm:outline-none",
    "uipm:font-['Inter']"
  ),
  variants: {
    state: {
      default: "",
      error: "",
      disabled: "uipm:opacity-50 uipm:cursor-not-allowed",
    }
  },
  defaultVariants: {
    state: "default",
  },
});

const countryCode = tv({
  base: cn(
    "uipm:flex uipm:flex-row uipm:items-center",
    "uipm:shrink-0"
  ),
});

export type PhoneInputProps = VariantProps<typeof phoneInput> &
  VariantProps<typeof phoneInputContainer> &
  Omit<ComponentProps<"input">, "type"> & {
    containerClassName?: string;
  };

/**
 * Phone input component with fixed +1 country code for US/Canada
 */
export function PhoneInput({
  className,
  containerClassName,
  state,
  background,
  disabled,
  ...props
}: PhoneInputProps) {
  const inputState = disabled ? "disabled" : state;
  const showErrorIcon = state === "error";

  return (
    <div className={cn(phoneInputWrapper(), containerClassName)}>
      <div className={phoneInputContainer({ background, state: inputState })}>
        <div className={countryCode()}>
          <span className="uipm:text-base uipm:text-[var(--color-color-accent)] uipm:font-['Inter'] uipm:whitespace-nowrap">
            +1
          </span>
        </div>
        <div className="uipm:w-px uipm:h-6 uipm:bg-[var(--color-color-light)]" />
        <input
          type="tel"
          disabled={disabled}
          {...props}
          className={cn(phoneInput({ state: inputState }), className)}
        />
      </div>
      {showErrorIcon && (
        <div className="uipm:absolute uipm:right-4 uipm:top-1/2 uipm:-translate-y-1/2 uipm:pointer-events-none">
          <ErrorIcon />
        </div>
      )}
    </div>
  );
}